import { useState, useEffect, useMemo } from 'react'
import { Tabs, Cell, Tag, Button, Popup, Toast, Loading, Empty, Dialog } from 'react-vant'
import { useNavigate } from 'react-router-dom'
import { useSelector } from 'react-redux'
import teacherAffairsApi from '../../../api/teacherAffairs'

const DUTY_TYPE_MAP = {
  winter_vacation: { label: '寒假值班', color: '#056b4f' },
  summer_vacation: { label: '暑假值班', color: '#fa8c16' },
  holiday: { label: '节日班', color: '#e11d48' },
}
const PERIOD_MAP = { morning: '上午', afternoon: '下午' }
const SOURCE_MAP = { self_register: '自主报名', admin_assign: '管理指派', auto_rotate: '自动轮值' }
const DAYS = ['日', '一', '二', '三', '四', '五', '六']

export default function DutyPage() {
  const navigate = useNavigate()
  const { userInfo } = useSelector(state => state.user)
  const [activeTab, setActiveTab] = useState(0)
  const [loading, setLoading] = useState(false)
  const [mySchedules, setMySchedules] = useState([])
  const [calendarData, setCalendarData] = useState({})
  const [windows, setWindows] = useState([])
  const [showSignUp, setShowSignUp] = useState(false)
  const [selectedWindow, setSelectedWindow] = useState(null)
  const [signUpDate, setSignUpDate] = useState('')
  const [signUpPeriod, setSignUpPeriod] = useState('morning')
  const [signUpNotes, setSignUpNotes] = useState('')

  // 当前月份
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear())
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth() + 1)

  useEffect(() => { fetchMySchedules(); fetchWindows() }, [])
  useEffect(() => { fetchCalendar() }, [currentYear, currentMonth])

  const fetchMySchedules = async () => {
    setLoading(true)
    try {
      const res = await teacherAffairsApi.getDutySchedules({ teacher_id: userInfo?.id })
      setMySchedules(Array.isArray(res) ? res : (res?.data || []))
    } catch (e) { console.error(e) }
    finally { setLoading(false) }
  }

  const fetchCalendar = async () => {
    try {
      const res = await teacherAffairsApi.getDutyCalendar(currentYear, currentMonth)
      setCalendarData(res || {})
    } catch (e) { console.error(e) }
  }

  const fetchWindows = async () => {
    try {
      const res = await teacherAffairsApi.getDutyWindows({ status: 'open' })
      setWindows(Array.isArray(res) ? res : (res?.data || []))
    } catch (e) { console.error(e) }
  }

  const getActiveWindowForDate = (dateStr) => {
    const now = new Date()
    const candidateWindows = windows.filter(w => {
      const isWithinDateRange = dateStr >= w.date_range_start && dateStr <= w.date_range_end
      return w.status === 'open' && isWithinDateRange
    })

    const first = candidateWindows[0]
    if (first) {
      const isRegActive = now > new Date(first.reg_start) && now < new Date(first.reg_end)
      return { ...first, isRegActive }
    }
    return null
  }

  const handleDateClick = (day) => {
    if (!day) return
    const dateStr = `${currentYear}-${String(currentMonth).padStart(2, '0')}-${String(day).padStart(2, '0')}`
    const activeWindow = getActiveWindowForDate(dateStr)
    
    if (!activeWindow) {
      Toast.info('该日期暂无开放的报名活动')
      return
    }
    if (!activeWindow.isRegActive) {
      Toast.info('当前不在该活动的报名时间内')
      return
    }
    
    setSelectedWindow(activeWindow)
    setSignUpDate(dateStr)
    setSignUpPeriod('morning')
    setSignUpNotes('')
    setShowSignUp(true)
  }

  // 日历网格
  const calendarGrid = useMemo(() => {
    const firstDay = new Date(currentYear, currentMonth - 1, 1).getDay()
    const daysInMonth = new Date(currentYear, currentMonth, 0).getDate()
    const cells = []
    for (let i = 0; i < firstDay; i++) cells.push(null)
    for (let d = 1; d <= daysInMonth; d++) cells.push(d)
    return cells
  }, [currentYear, currentMonth])

  const getDayDuties = (day) => {
    if (!day) return []
    const key = `${currentYear}-${String(currentMonth).padStart(2, '0')}-${String(day).padStart(2, '0')}`
    return calendarData[key] || []
  }

  // 本月我的值班次数（日历中 teacher_id 为本人的条目）
  const myDutyCount = useMemo(() => {
    return Object.values(calendarData || {}).reduce((sum, duties) => {
      if (!Array.isArray(duties)) return sum;
      return sum + duties.filter(d => d.teacher_id === userInfo?.id).length;
    }, 0);
  }, [calendarData, userInfo?.id]);

  const prevMonth = () => {
    if (currentMonth === 1) { setCurrentYear(y => y - 1); setCurrentMonth(12) }
    else setCurrentMonth(m => m - 1)
  }
  const nextMonth = () => {
    if (currentMonth === 12) { setCurrentYear(y => y + 1); setCurrentMonth(1) }
    else setCurrentMonth(m => m + 1)
  }

  const handleSignUp = async () => {
    if (!signUpDate) { Toast.info('请选择日期'); return }
    try {
      await teacherAffairsApi.signUpDuty({
        window_id: selectedWindow.id,
        duty_date: signUpDate,
        period: signUpPeriod,
        notes: signUpNotes,
      })
      Toast.success('报名成功')
      setShowSignUp(false)
      setSignUpDate(''); setSignUpNotes('')
      fetchMySchedules(); fetchCalendar()
    } catch (e) {
      Toast.fail(e.response?.data?.detail || '报名失败')
    }
  }

  const handleCancelSignUp = async (id) => {
    try {
      await Dialog.confirm({ title: '确认取消', message: '确认取消该值班报名？' })
      await teacherAffairsApi.cancelDutySignUp(id)
      Toast.success('取消成功')
      fetchMySchedules(); fetchCalendar()
    } catch (e) {
      if (e !== 'cancel') Toast.fail(e.response?.data?.detail || '取消失败')
    }
  }

  const today = new Date()
  const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`

  return (
    <div style={{ background: '#f5f8f6', minHeight: '100vh' }}>
      <div style={{
        background: '#056b4f', padding: '12px 16px',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      }}>
        <div onClick={() => navigate(-1)} style={{ cursor: 'pointer', padding: 4 }}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none"><path d="M15 18l-6-6 6-6" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
        </div>
        <span style={{ fontSize: 17, fontWeight: 700, color: '#fff' }}>我的值班</span>
        <div style={{ width: 24 }} />
      </div>

      {/* 概览 Banner（三页统一结构与高度） */}
      <div style={{ background: 'linear-gradient(135deg, #055e43 0%, #076f51 50%, #0c8c68 100%)', padding: '0 16px 28px', borderRadius: '0 0 24px 24px', color: '#fff' }}>
        <div style={{ display: 'flex', gap: 12 }}>
          <div style={{ flex: 1, background: 'rgba(255,255,255,0.2)', borderRadius: 14, padding: '14px 16px' }}>
            <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.85)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>本月值班</div>
            <div style={{ fontSize: 26, fontWeight: 700, color: '#fff', marginTop: 4 }}>{myDutyCount}<span style={{ fontSize: 13, fontWeight: 400 }}> 次</span></div>
          </div>
          <div style={{ flex: 1, background: 'rgba(255,255,255,0.2)', borderRadius: 14, padding: '14px 16px' }}>
            <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.85)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>我的排班</div>
            <div style={{ fontSize: 26, fontWeight: 700, color: '#fff', marginTop: 4 }}>{mySchedules.length}<span style={{ fontSize: 13, fontWeight: 400 }}> 条</span></div>
          </div>
          <div style={{ flex: 1, background: 'rgba(255,255,255,0.2)', borderRadius: 14, padding: '14px 16px' }}>
            <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.85)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>可报名</div>
            <div style={{ fontSize: 26, fontWeight: 700, color: '#fff', marginTop: 4 }}>{windows.length}<span style={{ fontSize: 13, fontWeight: 400 }}> 个</span></div>
          </div>
        </div>
      </div>

      <Tabs active={activeTab} onChange={setActiveTab} sticky swipeable
        color="#056b4f" titleActiveColor="#056b4f"
        style={{ '--rv-tabs-nav-background': '#fff' }}
      >
        {/* ===== 日历视图 ===== */}
        <Tabs.TabPane title="值班日历">
          <div style={{ margin: '12px', background: '#fff', borderRadius: 16, padding: 16, boxShadow: '0 2px 12px rgba(0,0,0,0.04)' }}>
            {/* 月份切换 */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <Button size="small" plain onClick={prevMonth}>◀</Button>
              <span style={{ fontSize: 16, fontWeight: 700 }}>{currentYear}年{currentMonth}月</span>
              <Button size="small" plain onClick={nextMonth}>▶</Button>
            </div>

            {/* 星期头 */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', textAlign: 'center', marginBottom: 4 }}>
              {DAYS.map(d => (
                <div key={d} style={{ fontSize: 12, color: '#94a3b8', padding: '4px 0' }}>{d}</div>
              ))}
            </div>

            {/* 日期格子 */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 2 }}>
              {calendarGrid.map((day, idx) => {
                const duties = getDayDuties(day)
                const dateStr = day ? `${currentYear}-${String(currentMonth).padStart(2, '0')}-${String(day).padStart(2, '0')}` : ''
                const isToday = dateStr === todayStr
                const hasMyDuty = duties.some(d => d.teacher_id === userInfo?.id)
                
                const activeWindow = day ? getActiveWindowForDate(dateStr) : null
                const isOpen = activeWindow && activeWindow.isRegActive

                return (
                  <div key={idx} onClick={() => handleDateClick(day)} style={{
                    minHeight: 44, padding: '2px 1px', borderRadius: 8,
                    background: isToday ? '#ecfdf5' : (hasMyDuty ? '#f0fdf4' : (isOpen ? '#f0fdf4' : 'transparent')),
                    border: isOpen && !isToday && !hasMyDuty ? '1px dashed #6ee7b7' : '1px solid transparent',
                    textAlign: 'center', position: 'relative',
                    cursor: day ? 'pointer' : 'default',
                  }}>
                    {day && (
                      <>
                        <div style={{
                          fontSize: 14, fontWeight: isToday ? 700 : 400,
                          color: isToday ? '#056b4f' : '#334155',
                        }}>{day}</div>
                        {duties.length > 0 && (
                          <div style={{ position: 'absolute', bottom: 2, left: '50%', transform: 'translateX(-50%)', display: 'flex', gap: 2 }}>
                            {duties.slice(0, 3).map((_, i) => (
                              <div key={i} style={{
                                width: 5, height: 5, borderRadius: '50%',
                                background: hasMyDuty ? '#00b578' : '#056b4f',
                              }} />
                            ))}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                )
              })}
            </div>

            {/* 图例 */}
            <div style={{ display: 'flex', gap: 16, marginTop: 12, fontSize: 12, color: '#94a3b8' }}>
              <span><span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: '#00b578', marginRight: 4 }} />我的值班</span>
              <span><span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: '#056b4f', marginRight: 4 }} />他人值班</span>
            </div>
          </div>
        </Tabs.TabPane>

        {/* ===== 我的排班 ===== */}
        <Tabs.TabPane title="我的排班">
          <div style={{ padding: '12px' }}>
            {loading ? (
              <div style={{ textAlign: 'center', padding: 40 }}><Loading size={24} /></div>
            ) : mySchedules.length === 0 ? (
              <Empty description="暂无排班记录" />
            ) : (
              mySchedules.map(item => (
                <div key={item.id} style={{
                  background: '#fff', borderRadius: 12, padding: 14, marginBottom: 10,
                  boxShadow: '0 1px 6px rgba(0,0,0,0.04)',
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ fontSize: 15, fontWeight: 600 }}>{item.duty_date}</span>
                      <Tag type="primary" plain style={{ fontSize: 11 }}>
                        {PERIOD_MAP[item.period]}
                      </Tag>
                    </div>
                    <Tag color={item.status === 'confirmed' ? '#00b578' : '#cbd5e1'}>
                      {item.status === 'confirmed' ? '已确认' : '已取消'}
                    </Tag>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ fontSize: 12, color: '#94a3b8' }}>
                      {DUTY_TYPE_MAP[item.duty_type]?.label || item.duty_type}
                      {' · '}
                      {SOURCE_MAP[item.source] || item.source}
                      {item.holiday_name && ` · ${item.holiday_name}`}
                    </div>
                    {item.source === 'self_register' && item.status === 'confirmed' && (
                      <Button size="mini" type="danger" plain onClick={() => handleCancelSignUp(item.id)}>
                        取消
                      </Button>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </Tabs.TabPane>

        {/* ===== 可报名 ===== */}
        <Tabs.TabPane title={`可报名(${windows.length})`}>
          <div style={{ padding: '12px' }}>
            {windows.length === 0 ? (
              <Empty description="暂无开放的报名窗口" />
            ) : (
              windows.map(w => (
                <div key={w.id} style={{
                  background: '#fff', borderRadius: 12, padding: 14, marginBottom: 10,
                  boxShadow: '0 1px 6px rgba(0,0,0,0.04)',
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                    <span style={{ fontSize: 15, fontWeight: 600, flex: 1, marginRight: 8 }}>{w.title}</span>
                    <Tag color={DUTY_TYPE_MAP[w.duty_type]?.color || '#056b4f'}>
                      {DUTY_TYPE_MAP[w.duty_type]?.label || w.duty_type}
                    </Tag>
                  </div>
                  <div style={{ fontSize: 12, color: '#94a3b8', marginBottom: 8, lineHeight: 1.8 }}>
                    <div>值班范围：{w.date_range_start} ~ {w.date_range_end}</div>
                    <div>已报名 {w.filled_slots || 0} 人</div>
                  </div>
                  <Button size="small" type="primary" block round
                    onClick={() => { setActiveTab(0); Toast.info('请在日历上点击要报名的日期') }}>
                    去日历选定日期
                  </Button>
                </div>
              ))
            )}
          </div>
        </Tabs.TabPane>
      </Tabs>

      {/* 报名弹窗 */}
      <Popup visible={showSignUp} position="bottom" round closeable
        onClose={() => setShowSignUp(false)}
        style={{ maxHeight: '75vh' }}>
        <div style={{ padding: '20px 16px 32px' }}>
          <h3 style={{ margin: '0 0 16px', fontSize: 17, fontWeight: 700 }}>值班报名</h3>

          {selectedWindow && (
            <div style={{
              background: '#f0fdf4', borderRadius: 10, padding: 12, marginBottom: 16,
              border: '1px solid #d1fae5',
            }}>
              <div style={{ fontWeight: 600, marginBottom: 4 }}>{selectedWindow.title}</div>
              <div style={{ fontSize: 12, color: '#64748b' }}>
                {selectedWindow.date_range_start} ~ {selectedWindow.date_range_end}
              </div>
            </div>
          )}

          <Cell title="值班日期" value={signUpDate || '请选择'} />
          <Cell title="值班时段" isLink
            value={PERIOD_MAP[signUpPeriod]}
            onClick={() => setSignUpPeriod(p => p === 'morning' ? 'afternoon' : 'morning')}
          />
          <div style={{ padding: '12px 16px' }}>
            <div style={{ fontSize: 14, color: '#475569', marginBottom: 8 }}>备注</div>
            <textarea
              value={signUpNotes}
              onChange={e => setSignUpNotes(e.target.value)}
              placeholder="可选填备注信息"
              style={{
                width: '100%', border: '1px solid #e2e8f0', borderRadius: 8,
                padding: 10, fontSize: 14, resize: 'none', height: 60,
                boxSizing: 'border-box',
              }}
            />
          </div>

          <div style={{ padding: '0 16px' }}>
            <Button type="primary" block round size="large" onClick={handleSignUp}>
              确认报名
            </Button>
          </div>
        </div>
      </Popup>
    </div>
  )
}
